import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, jsonBody } from "../../api/client";
import type { ListResponse, Location } from "../../api/types";
import { useWorkspaceLens, WorkspacePointer } from "../../app/workspaceLens";

import {
  Card,
  EditorPage,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  StatusPill,
} from "../../ui/components";
import { EditorPageSkeleton } from "../../ui/skeletons";
import type { AvailabilityRule, RateRule } from "./policyTypes";
import { useVenues } from "./queries";

interface QuestionDraft {
  id: string;
  key: string;
  label: string;
  kind: "text" | "textarea" | "choice" | "boolean";
  required: boolean;
  options: string;
}

function newQuestion(): QuestionDraft {
  return {
    id: crypto.randomUUID(),
    key: "",
    label: "",
    kind: "text",
    required: false,
    options: "",
  };
}

export function PublicOfferingEditorPage() {
  const { venueId = "" } = useParams<{ venueId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const lens = useWorkspaceLens();
  const venues = useVenues();
  const venue = venues.data?.items.find((item) => item.id === venueId);
  const locations = useQuery({
    queryKey: ["venues", venueId, "locations"],
    queryFn: () => api<ListResponse<Location>>(`/api/v1/venues/${venueId}/locations`),
    enabled: Boolean(venue),
  });
  const eligible = (locations.data?.items ?? []).filter(
    (location) => location.publicBookingEnabled && location.status === "active",
  );
  const [locationId, setLocationId] = useState("");
  const selectedLocationId = eligible.some((location) => location.id === locationId)
    ? locationId
    : (eligible[0]?.id ?? "");
  const [questions, setQuestions] = useState<QuestionDraft[]>([]);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const base =
    venue && selectedLocationId ? `/api/v1/venues/${venue.id}/locations/${selectedLocationId}` : "";
  const backTo = `/venues?venue=${encodeURIComponent(venueId)}`;
  const availability = useQuery({
    queryKey: ["venues", venueId, "locations", selectedLocationId, "availability-rules"],
    queryFn: () => api<ListResponse<AvailabilityRule>>(`${base}/availability-rules`),
    enabled: Boolean(base),
  });
  const rates = useQuery({
    queryKey: ["venues", venueId, "locations", selectedLocationId, "rate-rules"],
    queryFn: () => api<ListResponse<RateRule>>(`${base}/rate-rules`),
    enabled: Boolean(base),
  });

  function updateQuestion(id: string, patch: Partial<QuestionDraft>) {
    setQuestions((items) => items.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!venue || !selectedLocationId) return;
    const form = new FormData(event.currentTarget);
    const publishedOpening = availability.data?.items.some(
      (rule) => rule.kind === "opening_hours" && rule.published,
    );
    if (!publishedOpening) {
      setError(new Error("Add and publish opening hours for this space before putting it online."));
      return;
    }
    const requirePayment = form.get("requirePayment") === "on";
    const usableRate = rates.data?.items.some(
      (rate) => rate.active && (!rate.customerType || rate.customerType === "individual"),
    );
    if (requirePayment && !usableRate) {
      setError(
        new Error(
          "Add an active price for all customers or individual customers before requiring payment.",
        ),
      );
      return;
    }
    const payload = {
      locationId: selectedLocationId,
      mode: String(form.get("mode")),
      details: {
        headline: String(form.get("headline")),
        description: String(form.get("description")),
        minimumDurationMinutes: Number(form.get("minimumDurationMinutes")),
        maximumDurationMinutes: Number(form.get("maximumDurationMinutes")),
        slotIntervalMinutes: Number(form.get("slotIntervalMinutes")),
        holdMinutes: Number(form.get("holdMinutes")),
        minimumLeadMinutes: Number(form.get("minimumLeadMinutes")),
        maximumAdvanceDays: Number(form.get("maximumAdvanceDays")),
        allowWithoutOpeningHours: false,
        requirePayment,
        depositBasisPoints: Number(form.get("depositPercent")) * 100,
        questions: questions.map((question) => ({
          key: question.key.trim(),
          label: question.label.trim(),
          kind: question.kind,
          required: question.required,
          options:
            question.kind === "choice"
              ? question.options
                  .split(",")
                  .map((option) => option.trim())
                  .filter(Boolean)
              : [],
        })),
      },
      privacyNoticeVersion: String(form.get("privacyNoticeVersion")),
      termsVersion: String(form.get("termsVersion")),
      cancellationPolicyVersion: String(form.get("cancellationPolicyVersion")),
      publish: true,
    };
    setWorking(true);
    setError(undefined);
    try {
      await api("/api/v1/public-booking/configurations", {
        method: "POST",
        body: jsonBody(payload),
      });
      await queryClient.invalidateQueries({ queryKey: ["public-booking", "configurations"] });
      navigate(backTo);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (venues.isLoading || (venue && locations.isLoading)) return <EditorPageSkeleton />;
  if (venues.error || locations.error)
    return <ErrorPanel error={venues.error ?? locations.error} />;
  // Putting a space online is lettings work. Reached from the events side, this route says where
  // that work lives instead of showing a form whose every field is about hiring the hall out.
  if (lens.isEvents) {
    return (
      <EditorPage
        title="Publish online offering"
        description={venue?.name ?? "Online booking catalog"}
        backTo={backTo}
        backLabel="Back to venue"
      >
        <div className="stack">
          <EmptyState
            title="Online booking is managed in the Booking workspace"
            description="Publishing a space for customers to book online is part of letting the venue out, not of running your own programme."
          />
          <WorkspacePointer target="bookings">
            The online booking catalog, and everything published to it, lives there.
          </WorkspacePointer>
        </div>
      </EditorPage>
    );
  }

  return (
    <EditorPage
      title="Publish online offering"
      description={venue?.name ?? "Online booking catalog"}
      backTo={backTo}
      backLabel="Back to venue"
    >
      {!venue ? (
        <EmptyState
          title="Venue not found"
          description="The venue is outside your current access scope or no longer exists."
        />
      ) : (
        <div className="stack">
          {error ? <ErrorPanel error={error} /> : null}
          {!eligible.length ? (
            <Notice tone="warning">
              Mark at least one active location as eligible for public booking before publishing an
              offering.
            </Notice>
          ) : null}
          <Notice tone="info">
            Customers only see times that fit your opening hours and do not clash with another
            booking.
          </Notice>
          <form className="form-grid" onSubmit={submit}>
            <Field label="Location">
              <select
                name="locationId"
                required
                value={selectedLocationId}
                onChange={(event) => setLocationId(event.target.value)}
              >
                <option value="" disabled>
                  Select location
                </option>
                {eligible.map((location) => (
                  <option value={location.id} key={location.id}>
                    {venue.name} · {location.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="How should bookings be handled?">
              <select name="mode" defaultValue="request">
                <option value="request">Send requests to staff for approval</option>
                <option value="automatic">Confirm automatically when all checks pass</option>
              </select>
            </Field>
            {selectedLocationId ? (
              <div className="full inline">
                <StatusPill
                  value={
                    availability.data?.items.some(
                      (rule) => rule.kind === "opening_hours" && rule.published,
                    )
                      ? "opening hours ready"
                      : "opening hours required"
                  }
                />
                <StatusPill
                  value={
                    rates.data?.items.some((rate) => rate.active)
                      ? "pricing ready"
                      : "pricing optional"
                  }
                />
              </div>
            ) : null}
            <Field label="Headline">
              <input name="headline" required defaultValue="Book this space" />
            </Field>
            <Field label="Description">
              <textarea name="description" />
            </Field>
            <Field label="Shortest booking (minutes)">
              <input
                name="minimumDurationMinutes"
                type="number"
                min={15}
                defaultValue={60}
                required
              />
            </Field>
            <Field label="Longest booking (minutes)">
              <input
                name="maximumDurationMinutes"
                type="number"
                min={15}
                defaultValue={480}
                required
              />
            </Field>
            <Field label="Offer start times every (minutes)">
              <input name="slotIntervalMinutes" type="number" min={5} defaultValue={30} required />
            </Field>
            <Field label="Hold a selected time for (minutes)">
              <input name="holdMinutes" type="number" min={5} max={60} defaultValue={15} required />
            </Field>
            <Field label="Minimum notice (minutes)">
              <input name="minimumLeadMinutes" type="number" min={0} defaultValue={1440} required />
            </Field>
            <Field label="How far ahead customers can book (days)">
              <input
                name="maximumAdvanceDays"
                type="number"
                min={1}
                max={730}
                defaultValue={365}
                required
              />
            </Field>
            <Field label="Deposit percentage" hint="Enter 25 for a 25% deposit.">
              <input
                name="depositPercent"
                type="number"
                min={0}
                max={100}
                defaultValue={0}
                required
              />
            </Field>
            <label className="check-field">
              <input name="requirePayment" type="checkbox" /> Require payment
            </label>
            <Field
              label="Privacy notice label"
              hint="Use a short label for the wording customers agree to."
            >
              <input name="privacyNoticeVersion" defaultValue="demo-v1" required />
            </Field>
            <Field label="Terms label" hint="Use a short label for the wording customers agree to.">
              <input name="termsVersion" defaultValue="demo-v1" required />
            </Field>
            <Field
              label="Cancellation policy label"
              hint="Use a short label for the wording customers agree to."
            >
              <input name="cancellationPolicyVersion" defaultValue="demo-v1" required />
            </Field>
            <div className="full stack">
              <div className="split">
                <div>
                  <strong>Booking questions</strong>
                  <small>
                    Ask customers for any extra details you need after they choose a time.
                  </small>
                </div>
                <button
                  className="button secondary small"
                  type="button"
                  onClick={() => setQuestions((items) => [...items, newQuestion()])}
                >
                  <Plus size={15} /> Add question
                </button>
              </div>
              {questions.map((question) => (
                <Card key={question.id}>
                  <div className="form-grid">
                    <Field
                      label="Internal question name"
                      hint="For example: event_type. Use lowercase letters, numbers, and underscores."
                    >
                      <input
                        value={question.key}
                        onChange={(event) =>
                          updateQuestion(question.id, { key: event.target.value })
                        }
                        pattern="[a-z][a-z0-9_]{1,39}"
                        required
                      />
                    </Field>
                    <Field label="Question shown to customers">
                      <input
                        value={question.label}
                        onChange={(event) =>
                          updateQuestion(question.id, { label: event.target.value })
                        }
                        required
                      />
                    </Field>
                    <Field label="Type">
                      <select
                        value={question.kind}
                        onChange={(event) =>
                          updateQuestion(question.id, {
                            kind: event.target.value as QuestionDraft["kind"],
                          })
                        }
                      >
                        <option value="text">Short text</option>
                        <option value="textarea">Long text</option>
                        <option value="choice">Choice</option>
                        <option value="boolean">Yes or no</option>
                      </select>
                    </Field>
                    <Field
                      label="Choice options"
                      hint="Separate each answer with a comma. Only used for multiple-choice questions."
                    >
                      <input
                        value={question.options}
                        onChange={(event) =>
                          updateQuestion(question.id, { options: event.target.value })
                        }
                        disabled={question.kind !== "choice"}
                        required={question.kind === "choice"}
                      />
                    </Field>
                    <label className="check-field">
                      <input
                        type="checkbox"
                        checked={question.required}
                        onChange={(event) =>
                          updateQuestion(question.id, { required: event.target.checked })
                        }
                      />{" "}
                      Required
                    </label>
                    <button
                      className="button ghost small"
                      type="button"
                      onClick={() =>
                        setQuestions((items) => items.filter((item) => item.id !== question.id))
                      }
                    >
                      <Trash2 size={15} /> Remove
                    </button>
                  </div>
                </Card>
              ))}
            </div>
            <div className="full">
              <FormActions
                submitting={working}
                submitLabel="Publish offering"
                onCancel={() => navigate(backTo)}
              />
            </div>
          </form>
        </div>
      )}
    </EditorPage>
  );
}
